import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import { type HaulRoute } from 'api/haul-route.api';
import { formatRoadName } from './roadsImporter.helpers';

interface UnblockRoadDialogProps {
  target: HaulRoute | null;
  // Other currently-blocked routes that physically overlay `target` (see
  // findOverlappingRoutes). The view only opens this dialog when this is
  // non-empty — a plain one-way blocked road unblocks instantly from the
  // context menu with no dialog at all.
  overlappingBlockedRoutes: HaulRoute[];
  unblocking: boolean;
  onClose: () => void;
  onConfirm: (routeIds: string[]) => void;
}

export function UnblockRoadDialog({
  target,
  overlappingBlockedRoutes,
  unblocking,
  onClose,
  onConfirm,
}: UnblockRoadDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    if (!target) return;
    setSelectedIds([
      target.routeId,
      ...overlappingBlockedRoutes.map((r) => r.routeId),
    ]);
  }, [target, overlappingBlockedRoutes]);

  const toggleId = (routeId: string) => {
    setSelectedIds((prev) =>
      prev.includes(routeId)
        ? prev.filter((id) => id !== routeId)
        : [...prev, routeId]
    );
  };

  return (
    <Dialog
      open={target !== null}
      onClose={() => {
        if (!unblocking) onClose();
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {t('roads_importer_unblock_dialog_title')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('roads_importer_unblock_overlap_note', {
            count: overlappingBlockedRoutes.length,
          })}
        </Typography>
        {target && (
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedIds.includes(target.routeId)}
                onChange={() => {
                  toggleId(target.routeId);
                }}
                disabled={unblocking}
              />
            }
            label={formatRoadName(target.roadName ?? target.routeKey)}
          />
        )}
        {overlappingBlockedRoutes.map((route) => (
          <FormControlLabel
            key={route.routeId}
            control={
              <Checkbox
                checked={selectedIds.includes(route.routeId)}
                onChange={() => {
                  toggleId(route.routeId);
                }}
                disabled={unblocking}
              />
            }
            label={formatRoadName(route.roadName ?? route.routeKey)}
          />
        ))}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          disabled={unblocking}
          onClick={onClose}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('roads_importer_btn_cancel')}
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={
            unblocking ? (
              <CircularProgress size={14} />
            ) : (
              <LockOpenOutlinedIcon />
            )
          }
          disabled={unblocking || selectedIds.length === 0}
          onClick={() => {
            onConfirm(selectedIds);
          }}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('roads_importer_btn_unblock')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
